import React, { useState, useEffect, useCallback } from "react";
import {
  Users, BarChart2, CreditCard, Database, Activity,
  MessageSquare, RefreshCw, CheckCircle, XCircle,
  TrendingUp, Search, Send, FileCheck, Download, Inbox
} from "lucide-react";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

function getToken() { return localStorage.getItem("access_token"); }

async function adminFetch(path, options = {}, base = "my-admin") {
  const token = getToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API}/${base}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
  });
  if (res.status === 403) throw new Error("Super admin access required");
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `Request failed (${res.status})`); }
  return res.json();
}

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: BarChart2 },
  { id: "users", label: "Users", icon: Users },
  { id: "subscriptions", label: "Subscriptions", icon: CreditCard },
  { id: "revenue", label: "Revenue", icon: TrendingUp },
  { id: "sources", label: "Data Sources", icon: Database },
  { id: "documents", label: "Document Review", icon: FileCheck },
  { id: "submissions", label: "Tender Submissions", icon: Inbox },
  { id: "health", label: "System Health", icon: Activity },
  { id: "broadcast", label: "Broadcast", icon: MessageSquare },
  { id: "support", label: "Support Inbox", icon: MessageSquare },
];

// No free plan exists: TRIAL is the only no-payment state. The API reports plans in upper case
// (BASIC) but sometimes lower case (trial), so look colours up case-insensitively.
const PLAN_COLORS = { trial: "var(--text-muted)", basic: "var(--accent-blue)", professional: "var(--accent-green)", enterprise: "var(--accent-orange)" };
const planColor = (plan) => PLAN_COLORS[String(plan || "").toLowerCase()];

export default function AdminPanel({ tab }) {
  const [activeTab, setActiveTab] = useState(tab || "dashboard");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [search, setSearch] = useState("");
  const [broadcastMsg, setBroadcastMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  // Intentionally only watches `tab`: this syncs the incoming prop into local
  // state one-way. Adding activeTab here would fight the tab-click buttons
  // below, which call setActiveTab directly and would get immediately
  // reverted back to the `tab` prop on the very next render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab && tab !== activeTab) setActiveTab(tab); }, [tab]);

  const load = useCallback(async (tab) => {
    setLoading(true);
    try {
      let result;
      if (tab === "dashboard") result = await adminFetch("/dashboard");
      else if (tab === "users") result = await adminFetch(`/users?search=${encodeURIComponent(search)}&limit=50`);
      else if (tab === "subscriptions") result = await adminFetch("/subscriptions");
      else if (tab === "revenue") result = await adminFetch("/revenue");
      else if (tab === "sources") result = await adminFetch("/sources");
      else if (tab === "documents") result = await adminFetch("/documents/pending");
      else if (tab === "submissions") result = await adminFetch("/public-submissions/pending");
      else if (tab === "health") result = await adminFetch("/health");
      else if (tab === "support") result = await adminFetch("/support-messages");
      setData(prev => ({ ...prev, [tab]: result }));
    } catch (err) {
      if (err.message.includes("Super admin")) setAccessDenied(true);
      else toast.error(err.message);
    }
    setLoading(false);
  }, [search]);

  useEffect(() => { load(activeTab); }, [activeTab, load]);

  const handleUserAction = async (userId, action) => {
    try { await adminFetch(`/users/${userId}/action?action=${action}`, { method: "POST" }); toast.success(`User ${action}d`); load("users"); }
    catch (err) { toast.error(err.message); }
  };

  const handleUpgradePlan = async (companyId, plan) => {
    try { await adminFetch(`/subscriptions/${companyId}/upgrade?plan=${plan}`, { method: "POST" }); toast.success(`Upgraded to ${plan}`); load("subscriptions"); }
    catch (err) { toast.error(err.message); }
  };

  const handleToggleSource = async (sourceId) => {
    try { await adminFetch(`/sources/${sourceId}/toggle`, { method: "PATCH" }); toast.success("Source toggled"); load("sources"); }
    catch (err) { toast.error(err.message); }
  };

  const handleVerifyDocument = async (docId) => {
    try { await adminFetch(`/documents/${docId}/verify`, { method: "PATCH" }); toast.success("Document verified"); load("documents"); }
    catch (err) { toast.error(err.message); }
  };

  const handleRejectDocument = async (docId) => {
    const reason = window.prompt("Reason for rejection (shown to the company):");
    if (!reason || !reason.trim()) return;
    try {
      await adminFetch(`/documents/${docId}/reject`, { method: "PATCH", body: JSON.stringify({ reason: reason.trim() }) });
      toast.success("Document rejected");
      load("documents");
    } catch (err) { toast.error(err.message); }
  };

  const handleApproveSubmission = async (id) => {
    try { await adminFetch(`/public-submissions/${id}/approve`, { method: "PATCH" }); toast.success("Submission approved — now live"); load("submissions"); }
    catch (err) { toast.error(err.message); }
  };

  const handleRejectSubmission = async (id) => {
    const reason = window.prompt("Reason for rejection (emailed to the submitter):");
    if (!reason || !reason.trim()) return;
    try {
      await adminFetch(`/public-submissions/${id}/reject`, { method: "PATCH", body: JSON.stringify({ reason: reason.trim() }) });
      toast.success("Submission rejected");
      load("submissions");
    } catch (err) { toast.error(err.message); }
  };

  const handleDownloadDocument = async (docId, documentName) => {
    try {
      const result = await adminFetch(`/documents/${docId}/download`);
      const byteChars = atob(result.file_content_base64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: result.mime_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = result.document_name || documentName || "document";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const handleBroadcast = async () => {
    if (broadcastMsg.length < 10) { toast.error("Min 10 characters"); return; }
    setSending(true);
    try { const r = await adminFetch(`/broadcast?message=${encodeURIComponent(broadcastMsg)}`, { method: "POST" }); toast.success(`Sent to ${r.sent} users`); setBroadcastMsg(""); }
    catch (err) { toast.error(err.message); }
    setSending(false);
  };

  if (accessDenied) return (
    <div style={{ maxWidth: "500px", margin: "80px auto", textAlign: "center" }}>
      <XCircle size={48} color="#ff4d6d" style={{ marginBottom: 16 }} />
      <h2 style={{ fontSize: "22px", marginBottom: "8px" }}>Access Denied</h2>
      <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Super Admin role required to access this panel.</p>
    </div>
  );

  const d = data[activeTab] || {};

  return (
    <div style={{ maxWidth: "1100px" }}>
      <style>{`
  @media (max-width: 768px) {
    .mob-grid-2 { grid-template-columns: repeat(2,1fr) !important; }
    .mob-grid-1 { grid-template-columns: 1fr !important; }
    .mob-stack  { grid-template-columns: 1fr !important; }
    .mob-tabs   { overflow-x: auto !important; -webkit-overflow-scrolling: touch; width: 100% !important; max-width: 100vw; }
    .mob-tabs::-webkit-scrollbar { display: none; }
    .mob-tabs > * { white-space: nowrap; flex-shrink: 0; }
    .mob-table  { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%; }
    .mob-table table { min-width: 600px; }
  }
`}</style>
      <div style={{ marginBottom: "28px" }}>
        <h1 style={{ fontSize: "26px", marginBottom: "6px" }}>Admin Panel</h1>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Secure — authenticated via your session token</p>
      </div>

      <div className="mob-tabs" style={{ display: "flex", gap: "4px", marginBottom: "28px", background: "var(--bg-secondary)", padding: "4px", borderRadius: "12px", width: "fit-content", flexWrap: "wrap" }}>
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{ display: "flex", alignItems: "center", gap: "7px", padding: "8px 14px", borderRadius: "10px", border: "none", background: activeTab === id ? "var(--bg-card)" : "transparent", color: activeTab === id ? "var(--accent-green)" : "var(--text-secondary)", cursor: "pointer", fontSize: "12px", fontWeight: "600", transition: "all 0.15s" }}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {loading && <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "var(--text-muted)", fontSize: "13px", marginBottom: "16px" }}><RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} /> Loading...</div>}

      {activeTab === "dashboard" && d.total_users !== undefined && (
        <div>
          <div className="mob-grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
            {[["Total Users", d.total_users, `${d.active_users} active`], ["Companies", d.total_companies, `${d.plan_breakdown?.professional || 0} pro`], ["Total Tenders", (d.total_tenders||0).toLocaleString(), `${d.new_tenders_24h} today`], ["MRR", `₦${(d.mrr||0).toLocaleString()}`, "Monthly recurring"]].map(([label, value, sub]) => (
              <div key={label} className="card">
                <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "6px" }}>{label}</div>
                <div style={{ fontSize: "28px", fontWeight: "800", marginBottom: "4px" }}>{value}</div>
                <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{sub}</div>
              </div>
            ))}
          </div>
          <div className="mob-stack" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div className="card">
              <h3 style={{ fontSize: "14px", fontWeight: "700", marginBottom: "14px" }}>Plan Distribution</h3>
              {Object.entries(d.plan_breakdown || {}).map(([plan, count]) => (
                <div key={plan} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontSize: "13px", color: planColor(plan), fontWeight: "600", textTransform: "capitalize" }}>{plan}</span>
                  <span style={{ fontSize: "13px", fontWeight: "700" }}>{count} companies</span>
                </div>
              ))}
            </div>
            <div className="card">
              <h3 style={{ fontSize: "14px", fontWeight: "700", marginBottom: "14px" }}>System Health</h3>
              <div style={{ fontSize: "48px", fontWeight: "800", color: d.health_score >= 80 ? "var(--accent-green)" : d.health_score >= 50 ? "var(--accent-orange)" : "#ff4d6d", marginBottom: "4px" }}>{d.health_score}</div>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)", textTransform: "capitalize", marginBottom: "12px" }}>{d.health_status}</div>
              {[["Stale Scrapers", d.stale_scrapers], ["New Users (7d)", d.new_users_week], ["Sources", `${d.active_sources}/${d.total_sources}`]].map(([l, v]) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ color: "var(--text-secondary)" }}>{l}</span><span style={{ fontWeight: "600" }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "users" && (
        <div>
          <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
            <div style={{ position: "relative", flex: 1 }}>
              <Search size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && load("users")} placeholder="Search name or email..." style={{ paddingLeft: "36px", width: "100%" }} />
            </div>
            <button onClick={() => load("users")} className="btn btn-primary" style={{ padding: "10px 20px" }}>Search</button>
          </div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div className="mob-table"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead><tr style={{ borderBottom: "1px solid var(--border)" }}>{["Name", "Email", "Role", "Status", "Logins", "Action"].map(h => <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
              <tbody>
                {(d.users || []).map(u => (
                  <tr key={u.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px 16px", fontWeight: "600" }}>{u.full_name}</td>
                    <td style={{ padding: "12px 16px", color: "var(--text-secondary)" }}>{u.email}</td>
                    <td style={{ padding: "12px 16px" }}><span style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "100px", background: "var(--accent-green-dim)", color: "var(--accent-green)", fontWeight: "700", textTransform: "capitalize" }}>{u.role}</span></td>
                    <td style={{ padding: "12px 16px" }}><span style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "100px", background: u.is_active ? "rgba(0,212,161,0.1)" : "rgba(255,77,109,0.1)", color: u.is_active ? "var(--accent-green)" : "#ff4d6d", fontWeight: "600" }}>{u.is_active ? "Active" : "Suspended"}</span></td>
                    <td style={{ padding: "12px 16px", color: "var(--text-secondary)" }}>{u.login_count}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <select onChange={e => { if (e.target.value) { handleUserAction(u.id, e.target.value); e.target.value = ""; } }} style={{ fontSize: "12px", padding: "4px 8px", background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: "6px", cursor: "pointer", color: "var(--text-secondary)" }}>
                        <option value="">Action...</option>
                        <option value="suspend">Suspend</option>
                        <option value="activate">Activate</option>
                        <option value="make_admin">Make Admin</option>
                        <option value="delete">Delete</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        </div>
      )}

      {activeTab === "subscriptions" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead><tr style={{ borderBottom: "1px solid var(--border)" }}>{["Company", "Plan", "Active", "Users", "Upgrade"].map(h => <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
            <tbody>
              {(d.companies || []).map(c => (
                <tr key={c.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "12px 16px", fontWeight: "600" }}>{c.name}</td>
                  <td style={{ padding: "12px 16px" }}><span style={{ color: planColor(c.subscription_plan), fontWeight: "700", textTransform: "capitalize", fontSize: "12px" }}>{c.subscription_plan}</span></td>
                  <td style={{ padding: "12px 16px" }}>{c.is_subscription_active ? <CheckCircle size={16} color="var(--accent-green)" /> : <XCircle size={16} color="var(--text-muted)" />}</td>
                  <td style={{ padding: "12px 16px", color: "var(--text-secondary)" }}>{c.user_count}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <select onChange={e => { if (e.target.value) { handleUpgradePlan(c.id, e.target.value); e.target.value = ""; } }} style={{ fontSize: "12px", padding: "4px 8px", background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: "6px", cursor: "pointer" }}>
                      <option value="">Upgrade...</option>
                      <option value="basic">Basic</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "revenue" && d.mrr !== undefined && (
        <div>
          <div className="mob-grid-1" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "24px" }}>
            {[["MRR", `₦${(d.mrr||0).toLocaleString()}`], ["ARR", `₦${(d.arr||0).toLocaleString()}`], ["Paying Companies", d.paying_companies]].map(([l, v]) => (
              <div key={l} className="card" style={{ textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "8px" }}>{l}</div>
                <div style={{ fontSize: "30px", fontWeight: "800", color: "var(--accent-green)" }}>{v}</div>
              </div>
            ))}
          </div>
          <div className="card">
            <h3 style={{ fontSize: "14px", fontWeight: "700", marginBottom: "16px" }}>Revenue by Plan</h3>
            {(d.breakdown || []).map(row => (
              <div key={row.plan} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                <div><span style={{ fontWeight: "700", textTransform: "capitalize", color: planColor(row.plan) }}>{row.plan}</span><span style={{ color: "var(--text-muted)", fontSize: "12px", marginLeft: "8px" }}>{row.subscribers} × ₦{row.price.toLocaleString()}</span></div>
                <span style={{ fontWeight: "800", color: "var(--accent-green)" }}>₦{row.revenue.toLocaleString()}/mo</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "sources" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
            <thead><tr style={{ borderBottom: "1px solid var(--border)" }}>{["Source", "Key", "Tenders", "Last Scraped", "Status", "Toggle"].map(h => <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "10px", color: "var(--text-muted)", textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
            <tbody>
              {(d.sources || []).map(s => (
                <tr key={s.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "10px 14px", fontWeight: "600" }}>{s.name}</td>
                  <td style={{ padding: "10px 14px", fontFamily: "monospace", color: "var(--text-muted)", fontSize: "11px" }}>{s.scraper_key}</td>
                  <td style={{ padding: "10px 14px" }}>{s.total_tenders_scraped}</td>
                  <td style={{ padding: "10px 14px", color: s.is_stale ? "#ff8c42" : "var(--text-secondary)" }}>{s.last_scraped ? new Date(s.last_scraped).toLocaleDateString() : "Never"}{s.is_stale && " ⚠"}</td>
                  <td style={{ padding: "10px 14px" }}><span style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "100px", background: s.is_active ? "rgba(0,212,161,0.1)" : "rgba(255,255,255,0.05)", color: s.is_active ? "var(--accent-green)" : "var(--text-muted)", fontWeight: "700" }}>{s.is_active ? "Active" : "Paused"}</span></td>
                  <td style={{ padding: "10px 14px" }}><button onClick={() => handleToggleSource(s.id)} style={{ fontSize: "11px", padding: "4px 10px", background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: "6px", cursor: "pointer", color: "var(--text-secondary)" }}>{s.is_active ? "Pause" : "Activate"}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "documents" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "700" }}>Pending Document Verification</h3>
            <button onClick={() => load("documents")} className="btn btn-primary" style={{ padding: "8px 16px", fontSize: "12px" }}>Refresh</button>
          </div>
          {(d.documents || []).length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: "48px", color: "var(--text-muted)" }}>No documents awaiting review.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {(d.documents || []).map(doc => (
                <div key={doc.id} className="card" style={{ borderLeft: "3px solid var(--accent-orange)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px", flexWrap: "wrap", gap: "8px" }}>
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: "700" }}>{doc.document_type}</div>
                      <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                        {doc.company_name} · {doc.document_name} · {doc.file_size ? `${(doc.file_size / 1024).toFixed(0)}KB` : ""} · uploaded {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ""}
                      </div>
                      {doc.expiry_date && <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>Expires: {new Date(doc.expiry_date).toLocaleDateString()}</div>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button onClick={() => handleDownloadDocument(doc.id, doc.document_name)} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", padding: "6px 12px", background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: "6px", color: "var(--text-secondary)", cursor: "pointer" }}>
                        <Download size={12} /> View
                      </button>
                      <button onClick={() => handleVerifyDocument(doc.id)} style={{ fontSize: "11px", padding: "6px 12px", background: "var(--accent-green-dim)", border: "1px solid rgba(0,212,161,0.2)", borderRadius: "6px", color: "var(--accent-green)", cursor: "pointer", fontWeight: "600" }}>
                        Verify
                      </button>
                      <button onClick={() => handleRejectDocument(doc.id)} style={{ fontSize: "11px", padding: "6px 12px", background: "rgba(255,77,109,0.1)", border: "1px solid rgba(255,77,109,0.2)", borderRadius: "6px", color: "#ff4d6d", cursor: "pointer", fontWeight: "600" }}>
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "submissions" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "700" }}>Pending Tender Submissions</h3>
            <button onClick={() => load("submissions")} className="btn btn-primary" style={{ padding: "8px 16px", fontSize: "12px" }}>Refresh</button>
          </div>
          {(d.submissions || []).length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: "48px", color: "var(--text-muted)" }}>No submissions awaiting review.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {(d.submissions || []).map(s => (
                <div key={s.id} className="card" style={{ borderLeft: "3px solid var(--accent-orange)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px", flexWrap: "wrap", gap: "8px" }}>
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: "700" }}>{s.title}</div>
                      <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                        {s.submitter_organization} · {s.agency} · submitted by {s.submitter_name} ({s.submitter_email})
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                        {s.contract_value ? `₦${Number(s.contract_value).toLocaleString()} · ` : ""}
                        {s.submission_deadline ? `Deadline: ${new Date(s.submission_deadline).toLocaleDateString()} · ` : ""}
                        submitted {s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button onClick={() => handleApproveSubmission(s.id)} style={{ fontSize: "11px", padding: "6px 12px", background: "var(--accent-green-dim)", border: "1px solid rgba(0,212,161,0.2)", borderRadius: "6px", color: "var(--accent-green)", cursor: "pointer", fontWeight: "600" }}>
                        Approve &amp; Publish
                      </button>
                      <button onClick={() => handleRejectSubmission(s.id)} style={{ fontSize: "11px", padding: "6px 12px", background: "rgba(255,77,109,0.1)", border: "1px solid rgba(255,77,109,0.2)", borderRadius: "6px", color: "#ff4d6d", cursor: "pointer", fontWeight: "600" }}>
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "health" && d.health_score !== undefined && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div className="mob-grid-1" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
            <div className="card" style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "6px" }}>Health Score</div>
              <div style={{ fontSize: "48px", fontWeight: "800", color: d.health_score >= 80 ? "var(--accent-green)" : d.health_score >= 50 ? "var(--accent-orange)" : "#ff4d6d" }}>{d.health_score}</div>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)", textTransform: "capitalize" }}>{d.health_status}</div>
            </div>
            {Object.entries(d.metrics || {}).slice(0, 5).map(([key, val]) => (
              <div key={key} className="card">
                <div style={{ fontSize: "11px", color: "var(--text-muted)", marginBottom: "6px", textTransform: "capitalize" }}>{key.replace(/_/g, " ")}</div>
                <div style={{ fontSize: "22px", fontWeight: "800" }}>{val}</div>
              </div>
            ))}
          </div>
          {(d.health_issues || []).length > 0 && (
            <div className="card" style={{ background: "rgba(255,140,66,0.05)", border: "1px solid rgba(255,140,66,0.2)" }}>
              <h3 style={{ fontSize: "14px", fontWeight: "700", color: "var(--accent-orange)", marginBottom: "12px" }}>⚠ Active Issues</h3>
              {d.health_issues.map((issue, i) => <div key={i} style={{ fontSize: "13px", padding: "6px 0", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)" }}>• {issue}</div>)}
            </div>
          )}
        </div>
      )}

      {activeTab === "broadcast" && (
        <div className="card" style={{ maxWidth: "600px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "700", marginBottom: "8px" }}>WhatsApp Broadcast</h3>
          <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "20px" }}>Send a message to all users with WhatsApp alerts enabled.</p>
          <div style={{ marginBottom: "16px" }}>
            <textarea value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)} placeholder="Type your message... (min 10 characters)" rows={5}
              style={{ width: "100%", padding: "12px 14px", background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--text-primary)", fontSize: "13px", resize: "vertical", outline: "none", fontFamily: "inherit" }} />
            <div style={{ fontSize: "12px", color: "var(--text-muted)", textAlign: "right" }}>{broadcastMsg.length} characters</div>
          </div>
          <button onClick={handleBroadcast} disabled={sending || broadcastMsg.length < 10} className="btn btn-primary" style={{ padding: "12px 28px" }}>
            {sending ? <><RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} /> Sending...</> : <><Send size={14} /> Send Broadcast</>}
          </button>
        </div>
      )}

      {activeTab === "support" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "700" }}>Support Inbox</h3>
            <button onClick={() => load("support")} className="btn btn-primary" style={{ padding: "8px 16px", fontSize: "12px" }}>Refresh</button>
          </div>
          {(d.messages || []).length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: "48px", color: "var(--text-muted)" }}>No support messages yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {(d.messages || []).map(msg => (
                <div key={msg.id} className="card" style={{ borderLeft: `3px solid ${msg.status === "resolved" ? "var(--accent-green)" : "var(--accent-orange)"}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: "700" }}>{msg.subject}</div>
                      <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{msg.user_name} · {msg.user_email} · {msg.created_at ? new Date(msg.created_at).toLocaleDateString() : ""}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "100px", background: msg.status === "resolved" ? "rgba(0,212,161,0.1)" : "rgba(255,140,66,0.1)", color: msg.status === "resolved" ? "var(--accent-green)" : "var(--accent-orange)", fontWeight: "700" }}>{msg.status}</span>
                      {msg.status !== "resolved" && (
                        <button onClick={async () => {
                          try {
                            await adminFetch(`/support-messages/${msg.id}/resolve`, { method: "PATCH" });
                            load("support");
                            toast.success("Marked resolved");
                          } catch(e) { toast.error(e.message); }
                        }} style={{ fontSize: "11px", padding: "4px 10px", background: "var(--accent-green-dim)", border: "1px solid rgba(0,212,161,0.2)", borderRadius: "6px", color: "var(--accent-green)", cursor: "pointer" }}>
                          Mark Resolved
                        </button>
                      )}
                    </div>
                  </div>
                  <p style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.6", margin: 0 }}>{msg.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
